/**
 * THE WORKPAPER — the document that outlives the session.
 *
 * Every version is kept and every version is downloadable: v1 renders v1's
 * numbers forever, even after v2 has learned more. A document that silently
 * updated itself would be a view of what is concluded now rather than a record
 * of what was concluded then, and a file cannot tell the two apart.
 *
 * The screen builds nothing. The server renders the document from persisted
 * professional state and names it in the response; what lands on the desktop
 * is the file the server sent, under the name the server gave it. A workpaper
 * assembled in a browser tab is a screenshot with delusions of permanence.
 */
import { Download } from "lucide-react";
import { useState } from "react";
import type { WorkFile, WorkpaperVersion } from "@leveri/contracts";
import { Button, Citation, FileTitle, Notice, Reading, RuledList, Section, SectionHead } from "../../file/index.js";
import { failureMessage, save } from "../../api.js";

export function WorkpaperSection({ work }: { work: WorkFile }) {
  const [saving, setSaving] = useState<number | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  // The workbook is the accountant's file (owner, 2026-10-16: "I thought working paper was supposed to be an Excel");
  // the page is the same version, for reading or printing.
  async function download(version: WorkpaperVersion, kind: "xlsx" | "html") {
    if (saving !== null) return;
    setSaving(version.versionSeq);
    setFailure(null);
    try {
      await save(`/api/work/${work.id}/workpaper/${version.versionSeq}${kind === "xlsx" ? "?format=xlsx" : ""}`);
    } catch (error) {
      setFailure(failureMessage(error));
    } finally {
      setSaving(null);
    }
  }

  if (work.workpapers.length === 0) {
    return (
      <div>
        <FileTitle kicker="Workpaper" size={28}>No workpaper yet</FileTitle>
        <p className="mt-5 text-sm text-stone">No governed workpaper version exists for this work yet.</p>
      </div>
    );
  }

  return (
    <div>
      <FileTitle
        kicker="Workpaper"
        size={28}
        meta="Every figure in the workpaper is read from the governed professional state. What you download is what Leveri stands behind."
      >
        {work.title}
      </FileTitle>

      <Section>
        <SectionHead aside={String(work.workpapers.length)}>Versions</SectionHead>
        <RuledList>
          {work.workpapers.map((version) => (
            <div
              key={version.artifactVersionId}
              className="grid grid-cols-1 items-baseline gap-x-6 gap-y-2 py-3.5 md:grid-cols-[minmax(0,1fr)_auto]"
            >
              <div className="min-w-0">
                <div className="text-[15px] text-ink">
                  Version {version.versionSeq}
                  {/* A true flag renders as the name of the flag itself; a
                      false one renders nothing. The word is the field's, not
                      a judgment this screen made about the version. */}
                  {version.current && <span className="text-stone"> · current</span>}
                </div>
                <div className="mt-1" title={version.createdAt}><Citation>{version.createdAtText}</Citation></div>
                <Citation as="div" className="break-all">Content hash {version.contentHash}</Citation>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <Button variant={version.current ? "primary" : "secondary"} disabled={saving !== null} onClick={() => void download(version, "xlsx")} data-testid={`workpaper-xlsx-${version.versionSeq}`}>
                  <Download size={15} /> Excel
                </Button>
                <button type="button" disabled={saving !== null} onClick={() => void download(version, "html")} className="text-[13px] text-stone hover:text-ink disabled:opacity-60">
                  As a page
                </button>
              </div>
            </div>
          ))}
        </RuledList>
        {saving !== null && <div className="mt-3"><Reading>Preparing version {saving}…</Reading></div>}
        {failure && <div className="mt-3"><Notice>{failure}</Notice></div>}
      </Section>

      <details className="mt-7">
        <summary className="cursor-pointer list-none text-[13px] text-stone hover:text-ink">Technical provenance</summary>
        <RuledList className="mt-2 border-y border-rule">
          {work.workpapers.map((version) => (
            <div key={`provenance-${version.artifactVersionId}`} className="py-2.5">
              <Citation as="div" className="break-all">Version {version.versionSeq} · {version.artifactVersionId}</Citation>
              <Citation as="div" className="break-all">Artifact {version.artifactId}</Citation>
              <Citation as="div" className="break-all">Calculation {version.calculationRef ?? "—"}</Citation>
              <Citation as="div" className="break-all">Content contract {version.contentContractRef ?? "—"}</Citation>
            </div>
          ))}
        </RuledList>
      </details>
    </div>
  );
}
